import { useABAC } from '@adornis/abac/client/useABAC.js';
import {
  createPermissionPayload,
  Role,
  RoleAssignment,
  subscribeRoleAssignmentFor,
} from '@adornis/abac/policies/SimpleRolePolicy.js';
import { subscribeAllEntities } from '@adornis/baseql/operations/mongo.js';
import { guardDialog } from '@adornis/dialog/dialog-functions.js';
import type { SectionRendererSection } from '@adornis/entity-relationship-management/client/details/SectionRenderer.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { goTo } from '@adornis/router/client/open-href.js';
import type { AdornisUser } from '@adornis/users/db/a-user.js';
import { html, nothing } from 'lit';
import { subscribeAllowedPartnerCompanies } from '../../api/scoped-queries.js';
import type { SmartTeamsPartnerCompany } from '../../db/SmartTeamsCompany.js';
import { accessController } from '../../features/permissions/init-permissions.js';
import { SmartTeamsPermission, SmartTeamsRoleNames } from '../../features/permissions/roles-and-permissions.js';
import {
  addCompanyToScope,
  getCompanyIdsFromScope,
  removeCompanyFromScope,
} from '../../features/permissions/scope-helpers.js';

// Role Editor Component
const RoleEditor = stateful(({ user }: { user: AdornisUser<any> }) => {
  const [refreshKey, setRefreshKey] = useState(0);

  // Load all roles
  const rolesQuery = useQuery(() => subscribeAllEntities(Role)({ _id: 1, roleName: 1, permissions: 1 }), []);

  // Load user's role assignment (one per user)
  const assignmentQuery = useQuery<RoleAssignment | null>(
    () => subscribeRoleAssignmentFor(user._id)({ _id: 1, roleIds: 1, scope: { contexts: 1, permission: 1 } }),
    [refreshKey, user._id],
  );

  const roles = rolesQuery.value ?? [];
  const assignment = assignmentQuery.value;

  const selectedRoleIds = new Set(assignment?.roleIds ?? []);
  const selectedRoles = roles.filter(role => selectedRoleIds.has(role._id));

  const refresh = () => setRefreshKey(refreshKey + 1);

  const handleRoleSelectionChange = async (nextSelection: Role[]) => {
    const nextRoleIds = nextSelection.map(role => role._id);

    if (JSON.stringify([...selectedRoleIds].sort()) === JSON.stringify(nextRoleIds.sort())) return;

    await guardDialog(
      async () => {
        if (assignment) {
          // Update existing assignment
          assignment.roleIds = nextRoleIds;
          await assignment.save?.();
        } else if (nextRoleIds.length > 0) {
          // Create new assignment
          const newAssignment = new RoleAssignment({ userId: user._id, roleIds: nextRoleIds });
          await newAssignment.create?.();
        }
      },
      {
        loadingText: 'Roles are being updated…',
        successText: 'Roles updated.',
        errorMessagePrefix: 'Roles could not be updated',
      },
    );
    refresh();
  };

  const isLoading = rolesQuery.loading || assignmentQuery.loading;

  if (rolesQuery.error) {
    return html`<div class="alert alert-error">Roles could not be loaded: ${rolesQuery.error.message}</div>`;
  }

  return html`
    <div class="flex flex-col gap-2">
      <div class="flex items-center justify-between">
        <p class="text-xs text-base-content/70">Choose all roles that should be assigned to this user.</p>
        ${isLoading ? html`<span class="loading loading-spinner loading-sm text-primary"></span>` : nothing}
      </div>
      <div class="rounded-xl border border-base-200 bg-base-100">
        ${DropdownSelectionInput<Role, true>({
          multi: true,
          placeholder: 'Select roles …',
          selectables: roles as Role[],
          value: selectedRoles as Role[],
          renderString: role => role.roleName ?? role._id,
          resolveID: role => role._id,
          emptyState: 'No roles found',
          onPicked: value => handleRoleSelectionChange((value as Role[]) ?? []),
        })}
      </div>
    </div>
  `;
});

// Company Assignment Editor Component (only for Partner Managers)
const CompanyAssignmentEditor = stateful(({ user }: { user: AdornisUser<any> }) => {
  const { can: canReadCompanies } = useABAC(
    accessController,
    createPermissionPayload(SmartTeamsPermission.CompanyRead),
  );
  const [refreshKey, setRefreshKey] = useState(0);

  // Load all roles to check if user is Partner Manager
  const rolesQuery = useQuery(() => subscribeAllEntities(Role)({ _id: 1, roleName: 1 }), []);

  // Load user's role assignment
  const assignmentQuery = useQuery<RoleAssignment | null>(
    () => subscribeRoleAssignmentFor(user._id)({ _id: 1, roleIds: 1, scope: { contexts: 1, permission: 1 } }),
    [user._id, refreshKey],
  );

  // Load allowed companies (scoped by user permissions)
  const companiesQuery = useQuery(
    () => subscribeAllowedPartnerCompanies()({ _id: 1, name: 1, address: { zipCity: { city: 1 } } }),
    [],
  );

  const roles = rolesQuery.value ?? [];
  const assignment = assignmentQuery.value;
  const companies = companiesQuery.value ?? [];

  // Check if user has Partner Manager role
  const partnerManagerRole = roles.find(r => r.roleName === SmartTeamsRoleNames.PARTNER_MANAGER);
  const hasPartnerManagerRole = partnerManagerRole && assignment?.roleIds?.includes(partnerManagerRole._id);

  // Show info message if user doesn't have Partner Manager role
  if (!hasPartnerManagerRole) {
    return html`
      <p class="text-sm text-base-content/50">Only users with the role "Partner Manager" can assign companies.</p>
    `;
  }

  // Get assigned company IDs from scope (collected from all permission scopes)
  const assignedCompanyIds = new Set(getCompanyIdsFromScope(assignment?.scope ?? undefined));
  const selectedCompanies = companies.filter(c => assignedCompanyIds.has(c._id));
  const availableCompanies = companies.filter(c => !assignedCompanyIds.has(c._id));

  const refresh = () => setRefreshKey(refreshKey + 1);

  const navigateToCompany = (companyId: string) => {
    if (canReadCompanies) {
      void goTo(`/partner-companies/${companyId}`);
    }
  };

  const handleAddCompany = async (company: SmartTeamsPartnerCompany) => {
    await guardDialog(
      async () => {
        assignment!.scope = addCompanyToScope(assignment!.scope ?? undefined, company._id);
        await assignment!.save?.();
      },
      {
        loadingText: 'Partner company being added...',
        successText: 'Partner company added.',
        errorMessagePrefix: 'Partner company could not be added',
      },
    );
    refresh();
  };

  const handleRemoveCompany = async (companyId: string) => {
    await guardDialog(
      async () => {
        assignment!.scope = removeCompanyFromScope(assignment!.scope ?? undefined, companyId);
        await assignment!.save?.();
      },
      {
        loadingText: 'Partner company being removed...',
        successText: 'Partner company removed.',
        errorMessagePrefix: 'Partner company could not be removed',
      },
    );
    refresh();
  };

  const isLoading = companiesQuery.loading || assignmentQuery.loading;

  return html`
    <div class="flex flex-col gap-3">
      <div class="flex items-center justify-between">
        <p class="text-xs text-base-content/70">Partner companies this partner manager is responsible for.</p>
        ${isLoading ? html`<span class="loading loading-spinner loading-sm text-primary"></span>` : nothing}
      </div>

      <!-- Assigned Companies as Badges -->
      <div class="flex flex-wrap gap-2">
        ${selectedCompanies.length === 0
          ? html`<p class="text-sm text-base-content/50">No partner companies assigned</p>`
          : selectedCompanies.map(
              company => html`
                <div
                  class="badge badge-lg gap-2 py-3 pr-1 ${canReadCompanies ? 'cursor-pointer hover:badge-primary' : ''}"
                >
                  <div
                    class="flex items-center gap-2"
                    @click=${(e: Event) => {
                      e.stopPropagation();
                      navigateToCompany(company._id);
                    }}
                  >
                    <div class="avatar placeholder">
                      <div
                        class="bg-secondary text-secondary-content rounded-full w-5 h-5 flex items-center justify-center"
                      >
                        <span class="text-[10px] font-bold">${(company.name ?? '?')[0]?.toUpperCase() ?? '?'}</span>
                      </div>
                    </div>
                    <span
                      >${company.name}${company.address?.zipCity?.city
                        ? ` (${company.address.zipCity.city})`
                        : ''}</span
                    >
                  </div>
                  <button
                    class="btn btn-ghost btn-xs btn-circle"
                    @click=${(e: Event) => {
                      e.stopPropagation();
                      void handleRemoveCompany(company._id);
                    }}
                  >
                    <span class="icon-[mdi--close] text-sm"></span>
                  </button>
                </div>
              `,
            )}
      </div>

      <!-- Add Company Dropdown -->
      ${availableCompanies.length > 0
        ? html`
            <div class="w-64">
              ${DropdownSelectionInput<SmartTeamsPartnerCompany, false>({
                multi: false,
                placeholder: 'Add partner company',
                selectables: availableCompanies as SmartTeamsPartnerCompany[],
                value: undefined,
                renderString: company =>
                  `${company.name}${company.address?.zipCity?.city ? ` (${company.address.zipCity.city})` : ''}`,
                resolveID: company => company._id,
                emptyState: 'No partner companies found',
                onPicked: value => {
                  if (value) {
                    void handleAddCompany(value as SmartTeamsPartnerCompany);
                  }
                },
              })}
            </div>
          `
        : selectedCompanies.length > 0
          ? nothing
          : html`<p class="text-xs text-base-content/50">No partner companies available.</p>`}
    </div>
  `;
});

// Export separate sections
export const roleSection: SectionRendererSection<AdornisUser> = {
  title: 'Roles',
  render: ({ entity: user }) => {
    if (!user) return nothing;
    return RoleEditor({ user });
  },
};

export const companyAssignmentSection: SectionRendererSection<AdornisUser> = {
  title: 'Partner companies',
  render: ({ entity: user }) => {
    if (!user) return nothing;
    return CompanyAssignmentEditor({ user });
  },
};
